import React, { useRef, useEffect, useState, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import RevealSection from '../ui/RevealSection';

gsap.registerPlugin(ScrollTrigger);

const services = [
  {
    number: '01',
    title: 'Inteligencia Artificial',
    description:
      'Diseñamos e implementamos soluciones de IA con foco en gobernanza, adopción y retorno medible para el negocio.',
  },
  {
    number: '02',
    title: 'Logística',
    description:
      'Optimizamos operación, transporte y cadena de suministro con trazabilidad, planificación y ejecución orientadas a continuidad y eficiencia.',
  },
  {
    number: '03',
    title: 'Energía',
    description:
      'Operación energética en terreno, mantenimiento y soporte para infraestructura crítica en entornos de alta exigencia.',
  },
  {
    number: '04',
    title: 'Seguridad Ciberfísica',
    description:
      'Protegemos sistemas digitales y activos físicos críticos con una estrategia integrada de prevención, monitoreo y respuesta.',
  },
];

export default function Services() {
  const sectionRef = useRef(null);
  const isHoveringRef = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cycleSeed, setCycleSeed] = useState(0);

  const handleSelect = useCallback((index) => {
    setActiveIndex(index);
    setCycleSeed((seed) => seed + 1);
  }, []);

  useEffect(() => {
    const cards = sectionRef.current?.querySelectorAll('.service-card');
    if (!cards) return;

    cards.forEach((card, i) => {
      gsap.fromTo(
        card,
        { opacity: 0, y: 50, scale: 0.95 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          delay: i * 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: card,
            start: 'top 85%',
            toggleActions: 'play none none none',
          },
        }
      );
    });

    return () => ScrollTrigger.getAll().forEach((st) => st.kill());
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (isHoveringRef.current) return;
      setActiveIndex((prev) => (prev + 1) % services.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [cycleSeed]);

  return (
    <section id="services" ref={sectionRef} className="section py-32">
      <div className="max-w-6xl mx-auto w-full">
        <div className="mb-20 text-center">
          <RevealSection>
            <p className="text-white/40 text-sm font-body font-extralight uppercase tracking-[0.3em] mb-4">
              Lo Que Hacemos
            </p>
          </RevealSection>
          <h2 className="sr-only">Servicios y verticales</h2>
        </div>

        <div className="border-y border-white/10">
          {services.map((service, index) => {
            const isActive = index === activeIndex;

            return (
              <div
                key={service.number}
                role="button"
                tabIndex={0}
                onMouseEnter={() => {
                  isHoveringRef.current = true;
                  handleSelect(index);
                }}
                onMouseLeave={() => {
                  isHoveringRef.current = false;
                  setCycleSeed((seed) => seed + 1);
                }}
                onFocus={() => handleSelect(index)}
                onClick={() => handleSelect(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(index);
                  }
                }}
                className={`service-card group grid md:grid-cols-12 gap-5 md:gap-10 items-start py-10 md:py-14 px-2 md:px-4 border-b last:border-b-0 transition-all duration-500 cursor-pointer ${
                  isActive
                    ? 'opacity-100 border-white/[0.18] bg-white/[0.05]'
                    : 'opacity-28 border-white/[0.06] bg-transparent'
                }`}
              >
                <div className="md:col-span-2">
                  <span className={`font-display font-thin text-sm md:text-base tracking-[0.2em] transition-colors duration-500 ${
                    isActive ? 'text-white/[0.32]' : 'text-white/[0.06]'
                  }`}>{service.number}</span>
                </div>
                <h3 className={`md:col-span-4 font-display font-medium text-2xl md:text-3xl leading-tight transition-colors duration-500 ${
                  isActive ? 'text-white' : 'text-white/[0.14]'
                }`}>
                  {service.title}
                </h3>
                <p className={`md:col-span-6 font-body font-extralight leading-relaxed text-sm md:text-base max-w-2xl md:pr-8 transition-colors duration-500 ${
                  isActive ? 'text-white/[0.55]' : 'text-white/[0.10]'
                }`}>
                  {service.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
